/**
 * VOLUME ENGINE
 *
 * Computes weekly volume metrics per muscle group, intensity distribution,
 * compound/isolation ratio, and overall volume-load score.
 */

import type {
  Week,
  ExerciseInstance,
  MuscleGroup,
  MuscleVolumeMetric,
  VolumeMetrics,
  VolumeStatus,
  TrainingLevel,
  ALL_MUSCLE_GROUPS,
} from '../types';
import { ALL_MUSCLE_GROUPS as MUSCLES } from '../types';
import { getVolumeStandards } from '../data/volumeStandards';
import { parseReps, classifyIntensity } from '../utils/helpers';

// ── Indirect (secondary) volume weighting ───
const INDIRECT_SET_WEIGHT = 0.5; // secondary muscles get 50 % credit

// ── Status classification ───────────────────

function classifyVolume(
  totalSets: number,
  mev: number,
  mav: number,
  mrv: number
): VolumeStatus {
  if (totalSets < mev) return 'below_mev';
  if (totalSets < mev + (mav - mev) * 0.3) return 'at_mev';
  if (totalSets <= mav) return 'optimal';
  if (totalSets <= mrv) return 'approaching_mrv';
  return 'exceeds_mrv';
}

// ── Main calculator ─────────────────────────

export function calculateVolumeMetrics(
  week: Week,
  trainingLevel: TrainingLevel
): VolumeMetrics {
  const standards = getVolumeStandards(trainingLevel);
  const standardMap = new Map(standards.map((s) => [s.muscle, s]));

  // Accumulators
  const directSets: Record<string, number> = {};
  const indirectSets: Record<string, number> = {};
  let totalSets = 0;
  let compoundSets = 0;
  let isolationSets = 0;
  const intensityBins = { low: 0, moderate: 0, high: 0, max: 0 };
  let volumeLoadAccum = 0;

  for (const muscle of MUSCLES) {
    directSets[muscle] = 0;
    indirectSets[muscle] = 0;
  }

  // Walk every exercise in every session
  for (const session of week.sessions) {
    for (const ex of session.exercises) {
      const sets = ex.sets;
      const avgReps = parseReps(ex.reps);
      const rpe = ex.loadType === 'rpe' ? ex.load : ex.load / 10; // rough conversion

      totalSets += sets;

      // Tag accounting
      if (ex.tag === 'compound') compoundSets += sets;
      else isolationSets += sets;

      // Intensity
      const bin = classifyIntensity(rpe);
      intensityBins[bin] += sets;

      // Volume load  (sets × reps × intensity_weight)
      const intensityWeight = rpe / 10;
      volumeLoadAccum += sets * avgReps * intensityWeight;

      // Muscle accounting
      directSets[ex.primaryMuscle] = (directSets[ex.primaryMuscle] || 0) + sets;
      for (const sec of ex.secondaryMuscles) {
        indirectSets[sec] = (indirectSets[sec] || 0) + sets;
      }
    }
  }

  // Build per-muscle metrics
  const muscleVolumes: MuscleVolumeMetric[] = MUSCLES.map((muscle) => {
    const direct = directSets[muscle] || 0;
    const indirect = indirectSets[muscle] || 0;
    const weighted = direct + indirect * INDIRECT_SET_WEIGHT;
    const std = standardMap.get(muscle) || { mev: 0, mav: 10, mrv: 20 };

    return {
      muscle,
      directSets: direct,
      indirectSets: indirect,
      totalSets: Math.round(weighted * 10) / 10,
      mev: std.mev,
      mav: std.mav,
      mrv: std.mrv,
      status: classifyVolume(weighted, std.mev, std.mav, std.mrv),
    };
  });

  const compoundRatio = totalSets > 0 ? compoundSets / totalSets : 0;

  return {
    muscleVolumes,
    totalSets,
    compoundSets,
    isolationSets,
    compoundRatio,
    intensityDistribution: intensityBins,
    volumeLoadScore: Math.round(volumeLoadAccum),
  };
}

/**
 * Aggregate volume across multiple weeks (for plan-level overview).
 * Returns average weekly metrics.
 */
export function calculateAverageVolumeMetrics(
  weeks: Week[],
  trainingLevel: TrainingLevel
): VolumeMetrics | null {
  if (weeks.length === 0) return null;

  const allMetrics = weeks.map((w) => calculateVolumeMetrics(w, trainingLevel));
  const n = allMetrics.length;

  // Average each muscle's volumes
  const avgMuscleVolumes: MuscleVolumeMetric[] = MUSCLES.map((muscle) => {
    const items = allMetrics.map((m) => m.muscleVolumes.find((mv) => mv.muscle === muscle)!);
    const avgDirect = items.reduce((s, i) => s + i.directSets, 0) / n;
    const avgIndirect = items.reduce((s, i) => s + i.indirectSets, 0) / n;
    const avgTotal = items.reduce((s, i) => s + i.totalSets, 0) / n;
    const { mev, mav, mrv } = items[0];
    return {
      muscle,
      directSets: Math.round(avgDirect * 10) / 10,
      indirectSets: Math.round(avgIndirect * 10) / 10,
      totalSets: Math.round(avgTotal * 10) / 10,
      mev,
      mav,
      mrv,
      status: classifyVolume(avgTotal, mev, mav, mrv),
    };
  });

  return {
    muscleVolumes: avgMuscleVolumes,
    totalSets: Math.round(allMetrics.reduce((s, m) => s + m.totalSets, 0) / n),
    compoundSets: Math.round(allMetrics.reduce((s, m) => s + m.compoundSets, 0) / n),
    isolationSets: Math.round(allMetrics.reduce((s, m) => s + m.isolationSets, 0) / n),
    compoundRatio: allMetrics.reduce((s, m) => s + m.compoundRatio, 0) / n,
    intensityDistribution: {
      low: Math.round(allMetrics.reduce((s, m) => s + m.intensityDistribution.low, 0) / n),
      moderate: Math.round(allMetrics.reduce((s, m) => s + m.intensityDistribution.moderate, 0) / n),
      high: Math.round(allMetrics.reduce((s, m) => s + m.intensityDistribution.high, 0) / n),
      max: Math.round(allMetrics.reduce((s, m) => s + m.intensityDistribution.max, 0) / n),
    },
    volumeLoadScore: Math.round(allMetrics.reduce((s, m) => s + m.volumeLoadScore, 0) / n),
  };
}
